import WeeklyGrowthCard from '@/components/WeeklyGrowthCard';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award, Flame, Loader2, PartyPopper, Sparkles, Target, TrendingUp,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { apiGet, apiPost } from '@/lib/api';

/**
 * Phase 11 (student half) — private growth.
 *
 * You against your own past, and nobody else. No ranks, no class average, no
 * peers, no screen-time contest. Badges reward completion, improvement,
 * revision and mastery. Every chart says what it means and what to do next.
 */

const Panel = ({ title, icon: Icon, accent = 'indigo', children, testId }) => {
  const ring = {
    indigo: 'border-indigo-100', amber: 'border-amber-100',
    emerald: 'border-emerald-100', slate: 'border-slate-200',
  }[accent];
  const tint = {
    indigo: 'text-indigo-500', amber: 'text-amber-500',
    emerald: 'text-emerald-500', slate: 'text-slate-500',
  }[accent];
  return (
    <div className={`rounded-3xl border bg-white p-4 sm:p-5 ${ring}`} data-testid={testId}>
      <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
        <Icon className={`w-4 h-4 ${tint}`} /> {title}
      </p>
      <div className="mt-2.5">{children}</div>
    </div>
  );
};

export default function GrowthPage() {
  const [profile, setProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([
        apiGet('/premium/v1/growth/profile'),
        apiGet('/premium/v1/growth/analytics').catch(() => null),
      ]);
      setProfile(p); setAnalytics(a); setError('');
    } catch (e) { setError(e?.message || 'Your growth page is unavailable right now'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (badgeId) => {
    try { await apiPost(`/premium/v1/growth/badges/${badgeId}/seen`, {}); } catch { /* non-fatal */ }
    setProfile(p => ({ ...p, celebrations_pending: p.celebrations_pending.filter(c => c.badge_id !== badgeId) }));
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10" data-testid="growth-error">
        <p className="font-heading text-base text-slate-800">We couldn't open your growth page</p>
        <p className="font-body text-sm text-slate-500 mt-1">{error}</p>
        <button onClick={load} className="mt-3 text-sm font-heading text-indigo-600 hover:underline" data-testid="growth-retry">Try again</button>
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-slate-400" data-testid="growth-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> <span className="font-body text-sm">Adding up your progress…</span>
      </div>
    );
  }

  const chart = (key) => (analytics?.charts || []).find(c => c.key === key);
  const readiness = chart('readiness_trend');
  const focus = chart('focus_minutes');
  const heat = chart('concept_heatmap');
  const decay = chart('revision_decay');
  const effect = chart('intervention_effect');
  const subjects = chart('subject_trends');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="growth-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Your growth</h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          Compared with {profile.compared_with}. {analytics?.guardrail || ''}
        </p>
      </div>

      <WeeklyGrowthCard always />

      {profile.celebrations_pending.length > 0 && (
        <div className="rounded-3xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 text-white p-5" data-testid="growth-celebration">
          {profile.celebrations_pending.map(c => (
            <div key={c.badge_id} className="flex items-start gap-3">
              <PartyPopper className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-heading text-base font-bold">{c.label}</p>
                <p className="font-body text-sm text-white/90">{c.message}</p>
                <button onClick={() => dismiss(c.badge_id)} data-testid={`growth-celebration-dismiss-${c.badge_id}`}
                  className="mt-2 text-[11px] font-heading bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full">
                  Thanks — got it
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3">
        <Panel title="Your run" icon={Flame} accent="amber" testId="growth-streak">
          <p className="font-display text-3xl font-bold text-slate-900" data-testid="growth-streak-days">{profile.streak.current}</p>
          <p className="font-body text-[12px] text-slate-500">days in a row · best {profile.streak.best}</p>
        </Panel>
        <Panel title="Mastered" icon={Award} accent="emerald" testId="growth-mastered">
          <p className="font-display text-3xl font-bold text-slate-900">{profile.counts.mastered}</p>
          <p className="font-body text-[12px] text-slate-500">chapters / concepts at 80%+ · {profile.counts.weak} still shaky</p>
        </Panel>
        <Panel title="Portion" icon={Target} accent="indigo" testId="growth-portion">
          <p className="font-display text-3xl font-bold text-slate-900">{profile.counts.portion_pct}%</p>
          <p className="font-body text-[12px] text-slate-500">of everything planned, worked through</p>
        </Panel>
      </div>

      <Panel title="You, against you" icon={TrendingUp} testId="growth-self">
        <div className="space-y-1.5">
          <p className="font-body text-[13px] text-slate-700" data-testid="growth-self-mastery">{profile.self_vs_self.mastery.message}</p>
          <p className="font-body text-[11px] text-slate-400 -mt-1">{profile.self_vs_self.mastery.basis}</p>
          {profile.self_vs_self.activity && <p className="font-body text-[13px] text-slate-700" data-testid="growth-self-activity">{profile.self_vs_self.activity.message}</p>}
          <p className="font-body text-[13px] text-slate-700" data-testid="growth-self-items">{profile.self_vs_self.items_done.message}</p>
          <p className="font-body text-[13px] text-slate-700" data-testid="growth-self-revision">{profile.self_vs_self.revision_reliability.message}</p>
        </div>
      </Panel>

      <Panel title="Worth doing next" icon={Sparkles} testId="growth-challenges">
        <div className="grid sm:grid-cols-2 gap-2">
          {profile.challenges.map(c => (
            <button key={c.key} onClick={() => navigate(c.route)} data-testid={`growth-challenge-${c.key}`}
              className="text-left rounded-2xl border border-slate-200 p-2.5 hover:border-indigo-300 transition-colors">
              <p className="font-heading text-[13px] text-slate-800">{c.title}</p>
              <p className="font-body text-[11px] text-slate-500">{c.why}</p>
            </button>
          ))}
        </div>
      </Panel>

      <Panel title="Badges" icon={Award} accent="amber" testId="growth-badges">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {profile.badges.map(b => (
            <div key={b.badge_id} data-testid={`growth-badge-${b.badge_id}`}
              className={`rounded-2xl border p-2.5 ${b.earned ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200'}`}>
              <p className={`font-heading text-[12px] ${b.earned ? 'text-amber-900' : 'text-slate-600'}`}>{b.label}</p>
              <p className="font-body text-[10px] text-slate-500 mt-0.5">{b.hint}</p>
              {!b.earned && (
                <div className="mt-1.5 h-1 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${b.progress_pct}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>

      {readiness && readiness.data.length > 1 && (
        <Panel title={readiness.title} icon={TrendingUp} testId="growth-readiness-chart">
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={readiness.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="mid" stroke="#4f46e5" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="low" stroke="#a5b4fc" strokeWidth={1} dot={false} />
                <Line type="monotone" dataKey="high" stroke="#a5b4fc" strokeWidth={1} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="font-body text-[12px] text-slate-600 mt-1">{readiness.interpretation}</p>
        </Panel>
      )}

      {focus && focus.data.length > 0 && (
        <Panel title={focus.title} icon={TrendingUp} accent="slate" testId="growth-focus-chart">
          <div style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={focus.data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="minutes" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="font-body text-[12px] text-slate-600 mt-1">{focus.interpretation}</p>
        </Panel>
      )}

      {heat && (
        <Panel title={heat.title} icon={Target} testId="growth-heatmap">
          {heat.data.length ? heat.data.map(s => (
            <div key={s.subject} className="mb-2">
              <p className="font-heading text-[12px] text-slate-700">{s.subject}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {s.chapters.map(c => (
                  <span key={c.chapter} data-testid={`growth-heat-${c.chapter}`}
                    title={`${c.concepts} concept(s), ${c.weak} weak`}
                    className={`text-[10px] font-body px-2 py-1 rounded-lg border ${c.mastery >= 80 ? 'bg-emerald-100 border-emerald-200 text-emerald-900' : c.mastery >= 60 ? 'bg-amber-100 border-amber-200 text-amber-900' : 'bg-rose-100 border-rose-200 text-rose-900'}`}>
                    {c.chapter} · {c.mastery}%
                  </span>
                ))}
              </div>
            </div>
          )) : null}
          <p className="font-body text-[12px] text-slate-600">{heat.interpretation}</p>
          <button onClick={() => navigate(heat.next_action.route)} data-testid="growth-heatmap-action"
            className="mt-1.5 text-[11px] font-heading text-indigo-600 hover:underline">{heat.next_action.label} →</button>
        </Panel>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {[decay, effect, subjects].filter(Boolean).map(c => (
          <Panel key={c.key} title={c.title} icon={TrendingUp} accent="slate" testId={`growth-chart-${c.key}`}>
            <p className="font-body text-[13px] text-slate-700">{c.interpretation}</p>
            <button onClick={() => navigate(c.next_action.route)} data-testid={`growth-action-${c.key}`}
              className="mt-1.5 text-[11px] font-heading text-indigo-600 hover:underline">{c.next_action.label} →</button>
          </Panel>
        ))}
      </div>
    </div>
  );
}
